import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DIVISIONS } from "@/lib/roles";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";

export default function PublicLeadForm() {
  const [params] = useSearchParams();
  // ?ref=MOE-000042-2026 (employee) or MOP-…, MOF-… (partner). Optional.
  const refCode = (params.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", division: "academy", interest: "",
    notes: "", consent: false, website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to continue");
    if (!form.email && !form.phone) return toast.error("Please share an email or phone number");
    setSubmitting(true);
    try {
      await api.post("/public/leads",
        refCode ? { ...form, ref: refCode } : form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "var(--miro-navy)" }}>
      <img
        src="https://images.pexels.com/photos/10889988/pexels-photo-10889988.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0"
           style={{ background: "radial-gradient(700px at 20% 30%, rgba(14,165,255,0.35), transparent 65%), radial-gradient(600px at 80% 80%, rgba(95,191,58,0.25), transparent 65%)" }}/>

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="px-6 sm:px-12 py-6 flex items-center gap-3 text-white">
          <div className="w-11 h-11 rounded-lg bg-white flex items-center justify-center p-1">
            <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
          </div>
          <div>
            <div className="font-display font-black text-lg tracking-tight">MIRO Consultancy</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Learn. Earn. Grow.</div>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-12">
          <div className="w-full max-w-2xl">
            {done ? (
              <div data-testid="public-form-success" className="bg-white rounded-2xl p-10 text-center">
                <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
                     style={{ background: "rgba(95,191,58,0.15)" }}>
                  <CheckCircle2 size={30} className="text-[var(--miro-green-600)]"/>
                </div>
                <h1 className="font-display font-black text-3xl text-[var(--miro-navy)] mt-4 tracking-tight">
                  Thank you, {form.name.split(" ")[0]}
                </h1>
                <p className="text-[var(--miro-text-secondary)] mt-2">
                  Our team will reach out within 24 hours on the details you provided.
                </p>
                <Button
                  data-testid="public-form-reset-btn"
                  className="mt-6"
                  variant="outline"
                  onClick={() => { setDone(false); setForm({ name:"",email:"",phone:"",division:"academy",interest:"",notes:"",consent:false,website:""}); }}
                >
                  Submit another enquiry
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden">
                <div className="px-8 pt-8 pb-4">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                       style={{ background: "rgba(14,165,255,0.12)", color: "var(--miro-blue-600)" }}>
                    <Sparkles size={13}/> Talk to a MIRO advisor
                  </div>
                  <h1 className="font-display font-black text-3xl sm:text-4xl text-[var(--miro-navy)] tracking-tight mt-3">
                    Tell us what you're looking for
                  </h1>
                  <p className="text-[var(--miro-text-secondary)] mt-2">
                    Whether it's a course, a study abroad plan, wealth advice or a digital project — a
                    real human at MIRO will get back within one working day.
                  </p>
                  {refCode && (
                    <div
                      data-testid="public-lead-ref-banner"
                      className="mt-4 inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg"
                      style={{ background: "rgba(95,191,58,0.12)", color: "#166534" }}
                    >
                      <CheckCircle2 size={13}/>
                      Referred by <span className="font-mono">{refCode}</span>
                    </div>
                  )}
                </div>

                <form onSubmit={submit} className="px-8 pb-8 space-y-4">
                  <div>
                    <Label>Full name *</Label>
                    <Input data-testid="public-name" required value={form.name} onChange={(e)=>upd("name",e.target.value)} className="mt-1 h-11"/>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Email</Label>
                      <Input data-testid="public-email" type="email" value={form.email} onChange={(e)=>upd("email",e.target.value)} className="mt-1 h-11"/>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input data-testid="public-phone" value={form.phone} onChange={(e)=>upd("phone",e.target.value)} className="mt-1 h-11"/>
                    </div>
                  </div>
                  <div>
                    <Label>Which MIRO division interests you?</Label>
                    <Select value={form.division} onValueChange={(v)=>upd("division",v)}>
                      <SelectTrigger data-testid="public-division" className="mt-1 h-11"><SelectValue/></SelectTrigger>
                      <SelectContent>{DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>What are you interested in?</Label>
                    <Input data-testid="public-interest" placeholder="e.g. Data Science bootcamp, MBBS Georgia, retirement plan…"
                           value={form.interest} onChange={(e)=>upd("interest",e.target.value)} className="mt-1 h-11"/>
                  </div>
                  <div>
                    <Label>Anything else we should know?</Label>
                    <Textarea data-testid="public-notes" rows={3} value={form.notes} onChange={(e)=>upd("notes",e.target.value)} className="mt-1"/>
                  </div>
                  {/* Honeypot */}
                  <div className="hidden">
                    <Label>Website</Label>
                    <Input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e)=>upd("website",e.target.value)}/>
                  </div>

                  <label className="flex items-start gap-3 rounded-lg bg-[var(--miro-bg)] border border-[var(--miro-border)] p-3 cursor-pointer">
                    <Checkbox
                      data-testid="public-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => upd("consent", !!v)}
                    />
                    <span className="text-xs text-[var(--miro-text-secondary)] leading-snug">
                      I agree that MIRO Consultancy can contact me via email, phone or WhatsApp with information about
                      programs, offers and updates. I can opt out at any time. Wealth-related enquiries are workflow-only
                      and do not constitute investment advice.
                    </span>
                  </label>

                  <Button
                    data-testid="public-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 font-semibold text-base"
                    style={{ background: "var(--miro-navy)" }}
                  >
                    {submitting ? "Submitting…" : "Talk to an advisor"}
                  </Button>
                </form>
              </div>
            )}

            <div className="mt-8 text-center text-white/50 text-xs">
              Protected by MIRO One · Every lead is captured, assigned and responded to within SLA.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
